import React, { useRef, useCallback, useEffect, useState } from "react";
import styles from "../../styles/office/EditModal.module.css";
import { FaTimes, FaBuilding } from "react-icons/fa";

// Hooks
import { useModalState } from "./hooks/useModalState";
// import { usePdfGenerator } from "./hooks/usePdfGenerator";

// Dialog Components
import ConfirmationDialog from "./components/dialogs/ConfirmationDialog";
import AuthorizerConfirmationDialog from "./components/dialogs/AuthorizerConfirmationDialog";
import SurveyDetailsDialog from "./components/dialogs/SurveyDetailsDialog";
import DialogZone from "./components/dialogs/DialogZone";
import {
  RemarkAlertDialog,
  ZoneAlertDialog,
  UploadAlertDialog,
  PaymentAlertDialog
} from "./components/dialogs/AlertDialogs";

// Section Components
import ApplicantInfoSection from "./components/sections/ApplicantInfoSection";
import PaymentInfoSection from "./components/sections/PaymentInfoSection";
import ApplicationStatusSection from "./components/sections/ApplicationStatusSection";
import PropertyDetailsSection from "./components/sections/PropertyDetailsSection";

const EditModal = ({
  selectedRecord,
  closeEditModal,
  saveRecordChanges,
  language,
  officerProfile,
  signPath,
  villagePartsMap = {
    "Sangli Inner": ["Khanbhag", "Gavbhag", "Pethbhag", "Wakarbhag", "Extension Area"],
    "Sangli Outer": ["Khanbhag", "Gavbhag", "Pethbhag", "Wakarbhag", "Extension Area"],
    "Sangliwad Inner": ["Sangliwad"],
    "Kupwad Inner 1": ["Kupwad"],
    "Kupwad Inner 2": ["Kupwad"],
    "Kupwad Inner 3": ["Kupwad"],
    "Kupwad Outer": ["Kupwad"],
    "Miraj Outer": ["Miraj"],
    "Miraj Inner": ["Miraj"],
  } // Add this prop

}) => {
  // Custom hooks for state management
  const {
    localRecord,
    selectedFile,
    showConfirmation,
    showAuthorizerConfirmation,
    showuploadAlertDialog,
    showremarkAlertDialog,
    showZoneAlertDialog,
    formDataToSubmit,
    localRecordStatus,
    showPaymentAlertDialog,
    showSurveyDialog,
    showSurveyZoneDialog,
    zoneStatement,
    isGeneratingPDF,
    pdfGenerated,
    isLoading,
    progress,
    statusText,
    surveyFormData,
    setIsLoading,
    setProgress,
    setStatusText,
    setShowConfirmation,
    setAuthorizerShowConfirmation,
    setShowuploadAlertDialog,
    setShowremarkAlertDialog,
    setShowZoneAlertDialog,
    setFormDataToSubmit,
    setlocalRecordStatus,
    setShowPaymentAlertDialog,
    setShowSurveyDialog,
    setShowSurveyZoneDialog,
    setZoneStatement,
    handleSurveyInputChange,
    handleFieldChange,
    handleFileChange
  } = useModalState(selectedRecord);

  // PDF generation hook (keeping in case other parts use it)
  // const { generatePdfFromRef, generateMultiRefPdf } = usePdfGenerator();
  
  // Add this with your other state declarations
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  
  // EditModal.js - Add this state
  const [currentVillage, setCurrentVillage] = useState(selectedRecord.village || '');

  // Add a function to handle village change
  const handleVillageChange = (field, value) => {
    handleFieldChange(field, value);
    if (field === 'village') {
      setCurrentVillage(value);
    }
  };

  // Enhanced form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create form data
    const formData = new FormData();
    formData.append("request_unique_id", localRecord.request_unique_id);
    formData.append("id", localRecord.id);
    formData.append("status", localRecordStatus);
    formData.append("zoneStatementValue", localRecord.zoneStatementValue || '');
    formData.append("regionalPlanName", localRecord.regionalPlanName || '');
    formData.append("regionalPlanApprovalNumber", localRecord.regionalPlanApprovalNumber || '');
    formData.append("regionalPlanApprovalDate", localRecord.regionalPlanApprovalDate || '');
    formData.append("regionalPlanEffectiveDate", localRecord.regionalPlanEffectiveDate || '');
    formData.append("usageDepartment", localRecord.usageDepartment || '');
    formData.append("roadWidth", localRecord.roadWidth || '');
    formData.append("roadType", localRecord.roadType || '');
    formData.append("paymentDate", localRecord.paymentDate || '');
    formData.append("grnNumber", localRecord.grnNumber || '');
    formData.append("paymentVerified", localRecord.paymentVerified || '0');
    formData.append('signature_path', signPath || '');

    // ✅ Add village name to FormData
    formData.append("village", localRecord.village || '');

    // FIXED: Proper remark handling
    let finalRemark = localRecord.remark || '';
      console.log("localRecordStatus : 1:  ", localRecordStatus);

    // Check if this is an automatic approval that requires PDF generation
    const isAutoApproval = localRecordStatus === "approveda" ||
      localRecordStatus === "approveautomatically" ||
      localRecordStatus === "approvemanully";

    const isAutoApprovalAutomatically = localRecordStatus === "approveda" ||
      localRecordStatus === "approveautomatically";

    // Set appropriate remark for auto approvals
    if (isAutoApproval && !finalRemark) {

      console.log("isAutoApproval : 1:  ", isAutoApproval);
      finalRemark = 'Automatically approved by system';
      handleFieldChange('remark', finalRemark);
    }

    // Append the final remark
    formData.append("remark", finalRemark);

    // ✅ New validation: make Action mandatory
    if (!localRecord.action || localRecord.action.trim() === "") {
      alert(language === 'en'
        ? 'Please select an Action before submitting.'
        : 'सबमिट करण्यापूर्वी कृती निवडा.');
      setIsLoading(false);
      return;
    }

    // Manual file upload
    if (selectedFile) {
      formData.append("file", selectedFile);
    }

    // Validation checks
    if (localRecordStatus === "approved") {
      if (!selectedFile) {
        setShowuploadAlertDialog(true);
        setIsLoading(false);
        return;
      }
      if (!localRecord.paymentDate || !localRecord.grnNumber || !localRecord.paymentVerified) {
        setShowPaymentAlertDialog(true);
        setIsLoading(false);
        return;
      }
    }

    if (localRecordStatus === "sendforauthorization" ||
      localRecordStatus === "sendforauthorizationautomatically" ||
      localRecordStatus === "sendforauthorizationmanully") {
      const isZone = localRecord.certificatename === 'Zone';
      const hasZoneValue = localRecord.zoneStatementValue && localRecord.zoneStatementValue !== 'null';

      if (isZone && !hasZoneValue) {
        setShowZoneAlertDialog(true);
        setIsLoading(false);
        return;
      }
    } else if (localRecordStatus === "rejected" ||
      localRecordStatus === "reassigned" ||
      localRecordStatus === "sendbackforreview") {
      // FIXED: Proper remark validation for rejection cases
      if (!finalRemark || finalRemark.trim() === '') {
        setShowremarkAlertDialog(true);
        setIsLoading(false);
        return;
      }
    }

    setFormDataToSubmit(formData);
    if (officerProfile === 'Authorizer') {
      setAuthorizerShowConfirmation(true);
    } else {
      setShowConfirmation(true);
    }

    setIsLoading(false);
  };

  // Helper function for read-only fields
  const renderReadOnlyField = (label, value) => (
    <div className={styles.formGroup}>
      <label>{label}</label>
      {typeof value === 'string' ? (
        <input
          type="text"
          value={value === 'Rejected' ? 'Reassigned' : value}
          readOnly
          className={styles.readOnlyInput}
          data-original-value={value}
        />
      ) : (
        <div className={styles.readOnlyInput}>{value}</div>
      )}
    </div>
  );

  return (
    <div className={styles.modalOverlay}>
      {isLoading && (
        <div className={styles.loaderOverlay}>
          <div className={styles.loaderBox}>
            <p>{statusText}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaBuilding className={styles.modalIcon} />
            {language === 'en' ? 'Application Details' : 'अर्ज तपशील'}
          </h2>
          <button
            className={styles.modalCloseButton}
            onClick={closeEditModal}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.modalContent}>
            <div className={styles.modalColumn}>
              <PropertyDetailsSection
                language={language}
                localRecord={localRecord}
                selectedRecord={selectedRecord}
                handleFieldChange={handleVillageChange}
                renderReadOnlyField={renderReadOnlyField}
                villagePartsMap={villagePartsMap}
              />
            </div>

            <div className={styles.modalColumn}>
              <ApplicantInfoSection
                language={language}
                localRecord={localRecord}
                renderReadOnlyField={renderReadOnlyField}
              />
              <ApplicationStatusSection
                language={language}
                selectedRecord={selectedRecord}
                localRecord={localRecord}
                officerProfile={officerProfile}
                selectedFile={selectedFile}
                handleFieldChange={handleFieldChange}
                setlocalRecordStatus={setlocalRecordStatus}
                handleFileChange={handleFileChange}
                allHandleDownloadPDF={null} // Removed PDF download functionality
                renderReadOnlyField={renderReadOnlyField}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelButton} onClick={closeEditModal}>
              {language === 'en' ? 'Cancel' : 'रद्द करा'}
            </button>

            <button
              type="submit"
              className={styles.saveButton}
              disabled={isLoading}
            >
              {isLoading ? (language === 'en' ? 'Processing...' : 'प्रक्रिया करत आहे...') :
                (language === 'en' ? 'Save & Submit' : 'बदल जतन करा')}
            </button>
          </div>
        </form>
      </div>

      {showConfirmation && (
        <ConfirmationDialog
          language={language}
          onCancel={() => {
            if (!isDialogLoading) {
              setFormDataToSubmit(null);
              setShowConfirmation(false);
            }
          }}
          onConfirm={async () => {
            // Don't close dialog immediately - keep it open
            setIsDialogLoading(true); // Start loader
            try {
              await saveRecordChanges(formDataToSubmit);
              setFormDataToSubmit(null);
              setShowConfirmation(false); // Close dialog only after success
              closeEditModal();
            } catch (error) {
              console.error("Error saving changes:", error);
              alert(language === 'en' ? 'Error saving changes' : 'बदल जतन करताना त्रुटी');
              setShowConfirmation(false); // Close dialog on error
            } finally {
              setIsDialogLoading(false); // Stop loader
            }
          }}
          isLoading={isDialogLoading}
        />
      )}

      {showAuthorizerConfirmation && (
        <AuthorizerConfirmationDialog
          language={language}
          onCancel={() => {
            if (!isDialogLoading) {
              setFormDataToSubmit(null);
              setAuthorizerShowConfirmation(false);
            }
          }}
          onConfirm={async () => {
            // Don't close dialog immediately - keep it open
            setIsDialogLoading(true); // Start loader
            try {
              await saveRecordChanges(formDataToSubmit);
              setFormDataToSubmit(null);
              setAuthorizerShowConfirmation(false); // Close dialog only after success
              closeEditModal();
            } catch (error) {
              console.error("Error saving changes:", error);
              alert(language === 'en' ? 'Error saving changes' : 'बदल जतन करताना त्रुटी');
              setAuthorizerShowConfirmation(false); // Close dialog on error
            } finally {
              setIsDialogLoading(false); // Stop loader
            }
          }}
          isLoading={isDialogLoading}
        />
      )}

      {showPaymentAlertDialog && (
        <PaymentAlertDialog
          language={language}
          onCancel={() => setShowPaymentAlertDialog(false)}
          onConfirm={() => setShowPaymentAlertDialog(false)}
        />
      )}

      {showuploadAlertDialog && (
        <UploadAlertDialog
          language={language}
          onCancel={() => {
            setFormDataToSubmit(null);
            setShowuploadAlertDialog(false);
          }}
          onConfirm={() => setShowuploadAlertDialog(false)}
        />
      )}

      {showremarkAlertDialog && (
        <RemarkAlertDialog
          language={language}
          onCancel={() => {
            setFormDataToSubmit(null);
            setShowremarkAlertDialog(false);
          }}
          onConfirm={() => setShowremarkAlertDialog(false)}
        />
      )}

      {showZoneAlertDialog && (
        <ZoneAlertDialog
          language={language}
          onCancel={() => {
            setFormDataToSubmit(null);
            setShowZoneAlertDialog(false);
          }}
          onConfirm={() => setShowZoneAlertDialog(false)}
        />
      )}

      {showSurveyDialog && (
        <SurveyDetailsDialog
          language={language}
          formData={surveyFormData}
          onChange={handleSurveyInputChange}
          onCancel={() => setShowSurveyDialog(false)}
          onGenerate={() => {}} // Removed PDF download functionality
        />
      )}

      {showSurveyZoneDialog && (
        <DialogZone
          language={language}
          formData={surveyFormData}
          onChange={handleSurveyInputChange}
          onCancel={() => setShowSurveyZoneDialog(false)}
          onGenerate={() => {}} // Removed PDF download functionality
        />
      )}
    </div>
  );
};

export default EditModal;